import { eventStream, replayService, TQuote, TTickEvent } from '@smartcharts/core';
import { IChartEngine } from '../engine/IChartEngine';

export type TReplayControllerState = {
  isPlaying: boolean;
  speed: number; // ticks per second equivalent (approx)
  currentEpoch: number | null;
  startEpoch: number | null;
  endEpoch: number | null;
  progress: number; // 0-100
};

export interface IReplayController {
  loadHistorical(quotes: TQuote[], symbol: string, granularity: number): void;
  play(speed?: number): void;
  pause(): void;
  seek(epoch: number): void;
  stop(): void;
  setSpeed(speed: number): void;
  getState(): TReplayControllerState;
}

export class ReplayController implements IReplayController {
  private engine: IChartEngine;
  private isPlaying = false;
  private speed = 10; // default ticks/sec
  private currentEpoch: number | null = null;
  private startEpoch: number | null = null;
  private endEpoch: number | null = null;
  private iterator?: Generator<TTickEvent>;
  private frameHandle?: number;
  private pendingBatch: TTickEvent[] = [];

  constructor(engine: IChartEngine) { this.engine = engine; }

  loadHistorical(quotes: TQuote[], symbol: string, granularity: number): void {
    replayService.addHistorical(quotes, { symbol, granularity });
    const bounds = eventStream.getBounds();
    this.startEpoch = bounds.first;
    this.endEpoch = bounds.last;
    // Initialize chart with full history for now
    this.engine.resetSeries();
    this.engine.applyTickBatch(quotes, false);
    this.currentEpoch = bounds.last;
  }

  play(speed: number = this.speed): void {
    if (!this.startEpoch || !this.endEpoch) return;
    this.speed = speed;
    this.isPlaying = true;
    // Start from current or beginning
    const start = this.currentEpoch ?? this.startEpoch;
    this.iterator = eventStream.iterator({ startEpoch: start, endEpoch: this.endEpoch });
    this._scheduleFrame();
  }

  pause(): void {
    this.isPlaying = false;
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = undefined;
  }

  stop(): void {
    this.pause();
    this.currentEpoch = null;
    this.iterator = undefined;
    this.pendingBatch = [];
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  seek(epoch: number): void {
    if (!this.startEpoch || !this.endEpoch) return;
    this.pause();
    this.currentEpoch = epoch;
    // Rebuild to epoch: get window events and apply
    const events = eventStream.getTimeWindow(this.startEpoch, epoch);
    const quotes = eventStream.toQuotes(events);
    this.engine.resetSeries();
    this.engine.applyTickBatch(quotes, false);
  }

  getState(): TReplayControllerState {
    const progress = (!this.startEpoch || !this.endEpoch || !this.currentEpoch)
      ? 0
      : ((this.currentEpoch - this.startEpoch) / (this.endEpoch - this.startEpoch)) * 100;
    return {
      isPlaying: this.isPlaying,
      speed: this.speed,
      currentEpoch: this.currentEpoch,
      startEpoch: this.startEpoch,
      endEpoch: this.endEpoch,
      progress: Math.min(100, Math.max(0, progress)),
    };
  }

  private _scheduleFrame() {
    if (!this.isPlaying || !this.iterator) return;
    this.frameHandle = requestAnimationFrame(() => this._frame());
  }

  private _frame() {
    if (!this.iterator || !this.isPlaying) return;
    const batchSize = this._computeBatchSize();
    this.pendingBatch.length = 0;
    for (let i = 0; i < batchSize; i++) {
      const next = this.iterator.next();
      if (next.done) { this.isPlaying = false; break; }
      this.pendingBatch.push(next.value);
    }
    if (this.pendingBatch.length) {
      const quotes = eventStream.toQuotes(this.pendingBatch);
      this.engine.applyTickBatch(quotes, true);
      const last = this.pendingBatch[this.pendingBatch.length - 1];
      this.currentEpoch = last.epoch;
    }
    this._scheduleFrame();
  }

  private _computeBatchSize(): number {
    // Simple heuristic: speed ~ ticks/sec, assume 60fps => divide by 60
    const roughPerFrame = this.speed / 60;
    return Math.max(1, Math.floor(roughPerFrame) || 1);
  }
}

export const createReplayController = (engine: IChartEngine) => new ReplayController(engine);